import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
    HeartPulse, LayoutDashboard, Users, FileText,
    BarChart2, Target, Download, Settings, Menu, Bell, LogOut, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
    { name: 'Dashboard', href: '/app', icon: LayoutDashboard },
    { name: 'Profiles', href: '/app/profiles', icon: Users },
    { name: 'Assessments', href: '/app/assessments', icon: FileText },
    { name: 'Results', href: '/app/results', icon: BarChart2 },
    { name: 'IEP / Goals', href: '/app/iep', icon: Target },
    { name: 'Reports', href: '/app/reports', icon: Download },
    { name: 'Admin', href: '/app/admin', icon: Settings },
];

export function AppLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
    const location = useLocation();
    const { user, logout } = useAuth();

    return (
        <div className="min-h-screen bg-slate-50 flex font-inter">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`bg-white border-r border-slate-200 transition-all duration-300 ${sidebarOpen ? 'translate-x-0 w-72' : '-translate-x-full md:translate-x-0 md:w-24'} flex flex-col fixed inset-y-0 left-0 z-50 md:relative shadow-soft print:hidden`}>
                <div className="h-20 flex items-center justify-between px-6 border-b border-dashed border-slate-100">
                    <Link to="/" className="flex items-center gap-3 overflow-hidden">
                        <div className="h-10 w-10 rounded-xl bg-brand-600 flex items-center justify-center text-white shadow-premium shrink-0">
                            <HeartPulse className="h-6 w-6" />
                        </div>
                        {sidebarOpen && <span className="text-xl font-black tracking-tight text-slate-900 font-outfit uppercase">P2G Life</span>}
                    </Link>
                </div>

                <div className="flex-1 overflow-y-auto py-8 px-4 flex flex-col gap-1.5">
                    {sidebarOpen && <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-3">Main Menu</p>}
                    {navigation.map((item) => {
                        const isActive = location.pathname === item.href || (location.pathname.startsWith(item.href) && item.href !== '/app');
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => {
                                    if (window.innerWidth < 768) setSidebarOpen(false);
                                }}
                                title={!sidebarOpen ? item.name : undefined}
                                className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive ? 'bg-brand-600 text-white shadow-premium font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                            >
                                <item.icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-brand-600'} transition-colors`} />
                                {sidebarOpen && <span className="text-sm">{item.name}</span>}
                            </Link>
                        );
                    })}
                </div>

                <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                    <div className={`p-4 rounded-3xl bg-white border border-slate-100 shadow-soft flex items-center gap-3 ${!sidebarOpen && 'justify-center p-2'}`}>
                        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white font-bold flex-shrink-0 shadow-md">
                            {user?.name?.charAt(0) || 'U'}
                        </div>
                        {sidebarOpen && (
                            <div className="overflow-hidden flex-1 pl-1">
                                <p className="text-sm font-black text-slate-900 truncate leading-tight">{user?.name || 'User'}</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">{user?.role || 'Clinician'}</p>
                            </div>
                        )}
                        {sidebarOpen && (
                            <Button variant="ghost" size="icon" onClick={logout} className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0" title="Sign Out">
                                <LogOut className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Collapse Toggle Label */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="absolute -right-3 top-24 h-6 w-6 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-400 hover:text-brand-600 shadow-sm z-10 hidden md:flex"
                >
                    <ChevronLeft className={`h-3 w-3 transition-transform duration-300 ${!sidebarOpen ? 'rotate-180' : ''}`} />
                </button>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
                {/* Top Header */}
                <header className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 sm:px-8 flex items-center justify-between sticky top-0 z-30 print:hidden">
                    <div className="flex items-center gap-4">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="md:hidden" 
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu className="h-6 w-6 text-slate-700" />
                        </Button>
                        <h2 className="text-lg font-bold text-slate-900 font-outfit hidden md:block">
                            {navigation.find(n => location.pathname === n.href || (location.pathname.startsWith(n.href) && n.href !== '/app'))?.name || 'Dashboard'}
                        </h2>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="hidden sm:flex items-center bg-slate-100 rounded-xl p-1 shadow-inner h-10 w-64 border border-slate-200">
                            <div className="px-3 flex items-center text-slate-400">
                                <FileText className="h-4 w-4" />
                            </div>
                            <input type="text" placeholder="Global Search..." className="bg-transparent border-none focus:ring-0 text-sm w-full placeholder:text-slate-400 font-medium" />
                        </div>

                        <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-500 bg-white border border-slate-100 rounded-xl shadow-sm relative group">
                            <Bell className="h-5 w-5 group-hover:scale-110 transition-transform" />
                            <span className="absolute top-2.5 right-2.5 h-2 w-2 bg-brand-500 rounded-full ring-2 ring-white"></span>
                        </Button>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto p-6 sm:p-10 lg:p-12 relative print:p-0 print:m-0 print:overflow-visible">
                    <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-50/30 blur-[120px] rounded-full -z-10 pointer-events-none print:hidden"></div>
                    <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-accent-50/20 blur-[100px] rounded-full -z-10 pointer-events-none print:hidden"></div>
                    <div className="max-w-[1600px] mx-auto print:max-w-none print:w-full">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
}
